import { v4 as uuidv4 } from "uuid";
import { supabase } from "./supabase";
import { ChitPool, Loan, Member, Reminder, LoanReminder, NewMember } from "./types";
import { Database } from "./database.types";
import { format, addDays } from "date-fns";
import { useSubscription } from "@/lib/SubscriptionContext";

// Function to check if we're in development mode
const isDevelopmentMode = () => {
  return false; // Always use Supabase
};

export const getCurrentUser = async () => {
  const { data, error } = await supabase.auth.getUser();
  if (error) {
    console.error("Error getting user:", error);
    return null;
  }
  return data?.user || null;
};

// Function to map ChitPoolRow from the database to ChitPool type
const mapChitPoolRowToChitPool = (
  chitPoolRow: any,
  members: Member[]
): ChitPool => ({
  id: chitPoolRow.id,
  name: chitPoolRow.name,
  description: chitPoolRow.description || undefined,
  amount: chitPoolRow.amount,
  months: chitPoolRow.months,
  monthlyAmount: chitPoolRow.monthly_amount,
  postLiftAmount: chitPoolRow.post_lift_amount,
  firstDueDate: format(new Date(chitPoolRow.first_due_date), "yyyy-MM-dd"),
  createdAt: format(new Date(chitPoolRow.created_at), "yyyy-MM-dd'T'HH:mm:ss"),
  members: members.filter((member) => member.chitPoolId === chitPoolRow.id),
  sameAmountMonthly: chitPoolRow.same_amount_monthly !== undefined ? chitPoolRow.same_amount_monthly : true,
  monthlyAmounts: chitPoolRow.monthly_amounts || null,
});

// Function to map MemberRow from the database to Member type
const mapMemberRowToMember = (memberRow: any): Member => ({
  id: memberRow.id,
  chitPoolId: memberRow.chit_pool_id,
  name: memberRow.name,
  whatsappNumber: memberRow.whatsapp_number || memberRow.phone_number,
  hasLifted: memberRow.has_lifted,
  liftedDate: memberRow.lifted_date ? format(new Date(memberRow.lifted_date), "yyyy-MM-dd") : null,
  createdAt: memberRow.created_at ? format(new Date(memberRow.created_at), "yyyy-MM-dd'T'HH:mm:ss") : new Date().toISOString(),
  disbursedAmount: memberRow.disbursed_amount,
});

// Function to map LoanRow from the database to Loan type
const mapLoanRowToLoan = (loanRow: any): Loan => ({
  id: loanRow.id,
  customer_name: loanRow.customer_name,
  whatsappNumber: loanRow.whatsapp_number,
  amount: loanRow.amount,
  interest_rate: loanRow.interest_rate,
  tenure: loanRow.tenure,
  monthlyInterest: loanRow.monthly_interest || 0,
  firstDueDate: format(new Date(loanRow.first_due_date), "yyyy-MM-dd"),
  createdAt: format(new Date(loanRow.created_at), "yyyy-MM-dd'T'HH:mm:ss"),
});

// Function to map ReminderRow from the database to Reminder type
const mapReminderRowToReminder = (reminderRow: any): Reminder => ({
  id: reminderRow.id,
  chitPoolId: reminderRow.chit_pool_id || undefined,
  loanId: reminderRow.loan_id || undefined,
  message: reminderRow.message,
  reminderDate: format(new Date(reminderRow.reminder_date), "yyyy-MM-dd"),
  sent: reminderRow.sent || false,
  createdAt: reminderRow.created_at ? format(new Date(reminderRow.created_at), "yyyy-MM-dd'T'HH:mm:ss") : undefined,
});

// Get all chit pools
export const getChitPools = async (): Promise<ChitPool[]> => {
  try {
    const { data: chitPoolRows, error: chitPoolError } = await supabase
      .from("chit_pools")
      .select("*")
      .eq("is_deleted", false);

    if (chitPoolError) {
      console.error("Error fetching chit pools:", chitPoolError);
      throw new Error(chitPoolError.message);
    }

    const { data: memberRows, error: memberError } = await supabase
      .from("members")
      .select("*")
      .eq("is_deleted", false);

    if (memberError) {
      console.error("Error fetching members:", memberError);
      throw new Error(memberError.message);
    }

    if (!chitPoolRows) return [];

    // Map MemberRows to Member type
    const members: Member[] = memberRows
      ? memberRows.map(mapMemberRowToMember)
      : [];

    // Map ChitPoolRows to ChitPool type and include members
    const chitPools: ChitPool[] = chitPoolRows.map((chitPoolRow) =>
      mapChitPoolRowToChitPool(chitPoolRow, members)
    );

    return chitPools;
  } catch (error: any) {
    console.error("Error fetching chit pools:", error);
    throw new Error(error.message);
  }
};

// Get chit pool by ID
export const getChitPoolById = async (id: string): Promise<ChitPool | null> => {
  try {
    const { data: chitPoolRow, error: chitPoolError } = await supabase
      .from("chit_pools")
      .select("*")
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (chitPoolError) {
      console.error("Error fetching chit pool:", chitPoolError);
      throw new Error(chitPoolError.message);
    }

    if (!chitPoolRow) return null;

    const { data: memberRows, error: memberError } = await supabase
      .from("members")
      .select("*")
      .eq("chit_pool_id", id)
      .eq("is_deleted", false);

    if (memberError) {
      console.error("Error fetching members:", memberError);
      throw new Error(memberError.message);
    }

    // Map MemberRows to Member type
    const members: Member[] = memberRows
      ? memberRows.map(mapMemberRowToMember)
      : [];

    // Map ChitPoolRow to ChitPool type and include members
    const chitPool: ChitPool = mapChitPoolRowToChitPool(chitPoolRow, members);

    return chitPool;
  } catch (error: any) {
    console.error("Error fetching chit pool:", error);
    throw new Error(error.message);
  }
};

// Create chit pool
export const createChitPool = async (chitPool: Omit<ChitPool, 'id' | 'createdAt' | 'members'> & { sameAmountMonthly?: boolean; monthlyAmounts?: { month: number; amount: number }[] | null }): Promise<ChitPool> => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("User not authenticated");

    // Check subscription limits
    const { data: chitPoolsData, error: countError } = await supabase
      .from("chit_pools")
      .select("id")
      .eq("user_id", user.id)
      .eq("is_deleted", false);
    
    if (!countError) {
      // Get the current plan for the user
      const { data: subscriptionData, error: subError } = await supabase
        .from("subscriptions")
        .select("*, plan:plans(*)")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      let maxChitFunds = 1;  // Default to free plan limit
      let planName = "Free";
      
      if (!subError && subscriptionData) {
        maxChitFunds = subscriptionData.plan.max_chit_funds;
        planName = subscriptionData.plan.name;
      } else {
        // Get free plan limit
        const { data: freePlan, error: planError } = await supabase
          .from("plans")
          .select("max_chit_funds, name")
          .eq("price", 0)
          .single();
        
        if (!planError && freePlan) {
          maxChitFunds = freePlan.max_chit_funds;
          planName = freePlan.name;
        }
      }
      
      if (chitPoolsData && chitPoolsData.length >= maxChitFunds) {
        throw new Error(`You have reached your ${planName} plan limit of ${maxChitFunds} chit ${maxChitFunds === 1 ? "fund" : "funds"}. Please upgrade your plan to create more.`);
      }
    }

    console.log("Creating chit pool with user:", user.id);

    const { data, error } = await supabase
      .from("chit_pools")
      .insert([
        {
          name: chitPool.name,
          description: chitPool.description || null,
          amount: chitPool.amount,
          months: chitPool.months, 
          monthly_amount: chitPool.monthlyAmount,
          post_lift_amount: chitPool.postLiftAmount,
          first_due_date: new Date(chitPool.firstDueDate).toISOString(),
          user_id: user.id,
          is_deleted: false,
          same_amount_monthly: chitPool.sameAmountMonthly !== undefined ? chitPool.sameAmountMonthly : true,
          monthly_amounts: chitPool.monthlyAmounts || null,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error("Error creating chit pool:", error);
      throw new Error(error.message);
    }

    if (!data) {
      throw new Error("No data returned after creating chit pool");
    }

    return {
      id: data.id,
      name: data.name,
      description: data.description || undefined,
      amount: data.amount,
      months: data.months,
      monthlyAmount: data.monthly_amount,
      postLiftAmount: data.post_lift_amount,
      firstDueDate: format(new Date(data.first_due_date), "yyyy-MM-dd"),
      createdAt: format(new Date(data.created_at), "yyyy-MM-dd'T'HH:mm:ss"),
      members: [],
    };
  } catch (error: any) {
    console.error("Error creating chit pool:", error);
    throw new Error(error.message);
  }
};

// Update chit pool
export const updateChitPool = async (id: string, chitPool: Partial<Omit<ChitPool, 'id' | 'createdAt' | 'members'>>): Promise<ChitPool> => {
  try {
    const { data, error } = await supabase
      .from("chit_pools")
      .update({
        name: chitPool.name,
        description: chitPool.description,
        amount: chitPool.amount,
        months: chitPool.months,
        monthly_amount: chitPool.monthlyAmount,
        post_lift_amount: chitPool.postLiftAmount,
        first_due_date: chitPool.firstDueDate ? new Date(chitPool.firstDueDate).toISOString() : undefined,
      })
      .eq("id", id)
      .eq("is_deleted", false)
      .select()
      .single();

    if (error) {
      console.error("Error updating chit pool:", error);
      throw new Error(error.message);
    }

    if (!data) throw new Error("Chit pool not found");

    const { data: memberRows, error: memberError } = await supabase
      .from("members")
      .select("*")
      .eq("chit_pool_id", id)
      .eq("is_deleted", false);

    if (memberError) {
      console.error("Error fetching members:", memberError);
      throw new Error(memberError.message);
    }

    // Map MemberRows to Member type
    const members: Member[] = memberRows
      ? memberRows.map(mapMemberRowToMember)
      : [];

    return {
      id: data.id,
      name: data.name,
      description: data.description,
      amount: data.amount,
      months: data.months,
      monthlyAmount: data.monthly_amount,
      postLiftAmount: data.post_lift_amount,
      firstDueDate: format(new Date(data.first_due_date), "yyyy-MM-dd"),
      createdAt: format(new Date(data.created_at), "yyyy-MM-dd'T'HH:mm:ss"),
      members: members,
    };
  } catch (error: any) {
    console.error("Error updating chit pool:", error);
    throw new Error(error.message);
  }
};

// Delete chit pool (soft delete)
export const deleteChitPool = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from("chit_pools")
      .update({ is_deleted: true })
      .eq("id", id);

    if (error) {
      console.error("Error deleting chit pool:", error);
      throw new Error(error.message);
    }
  } catch (error: any) {
    console.error("Error deleting chit pool:", error);
    throw new Error(error.message);
  }
};

// Create member
export const createMember = async (member: NewMember): Promise<Member> => {
  try {
    console.log("Creating member:", member);
    
    const { data, error } = await supabase
      .from("members")
      .insert([
        {
          chit_pool_id: member.chitPoolId,
          name: member.name,
          whatsapp_number: member.whatsappNumber,
          has_lifted: member.hasLifted || false,
          lifted_date: member.liftedDate || null,
          disbursed_amount: member.disbursedAmount || null,
          is_deleted: false,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error("Error creating member:", error);
      throw new Error(error.message);
    }

    if (!data) {
      throw new Error("No data returned after creating member");
    }

    return {
      id: data.id,
      chitPoolId: data.chit_pool_id,
      name: data.name,
      whatsappNumber: data.whatsapp_number,
      hasLifted: data.has_lifted,
      liftedDate: data.lifted_date ? format(new Date(data.lifted_date), "yyyy-MM-dd") : null,
      createdAt: format(new Date(data.created_at), "yyyy-MM-dd'T'HH:mm:ss"),
      disbursedAmount: data.disbursed_amount
    };
  } catch (error: any) {
    console.error("Error creating member:", error);
    throw new Error(error.message);
  }
};

// Update member
export const updateMember = async (
  id: string, 
  data: { 
    name?: string; 
    whatsappNumber?: string; 
    chitPoolId?: string;
    disbursedAmount?: number | null;
  }
): Promise<Member> => {
  try {
    // Prepare the data to update
    const updateData: Record<string, any> = {};
    
    if (data.name !== undefined) updateData.name = data.name;
    if (data.whatsappNumber !== undefined) updateData.whatsapp_number = data.whatsappNumber;
    
    // Explicitly handle disbursed amount to ensure it's properly updated
    if (data.disbursedAmount !== undefined) {
      updateData.disbursed_amount = data.disbursedAmount;
    }
    
    // Execute the update
    const { data: updatedMember, error } = await supabase
      .from("members")
      .update(updateData)
      .eq("id", id)
      .eq("is_deleted", false)
      .select()
      .single();
    
    if (error) {
      console.error("Error updating member:", error);
      throw new Error(error.message);
    }
    
    // Map the updated member back to the Member type
    return {
      id: updatedMember.id,
      name: updatedMember.name,
      whatsappNumber: updatedMember.whatsapp_number,
      hasLifted: updatedMember.has_lifted,
      liftedDate: updatedMember.lifted_date,
      createdAt: updatedMember.created_at,
      chitPoolId: updatedMember.chit_pool_id,
      disbursedAmount: updatedMember.disbursed_amount
    };
  } catch (error: any) {
    console.error("Error updating member:", error);
    throw new Error(error.message);
  }
};

// Delete member (soft delete)
export const deleteMember = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from("members")
      .update({ is_deleted: true })
      .eq("id", id);

    if (error) {
      console.error("Error deleting member:", error);
      throw new Error(error.message);
    }
  } catch (error: any) {
    console.error("Error deleting member:", error);
    throw new Error(error.message);
  }
};

// Toggle member lift status
export const toggleMemberLift = async (id: string, hasLifted: boolean, disbursedAmount: number | null = null): Promise<Member> => {
  try {
    const { data, error } = await supabase
      .from("members")
      .update({
        has_lifted: hasLifted,
        lifted_date: hasLifted ? new Date().toISOString() : null,
        disbursed_amount: hasLifted ? disbursedAmount : null
      })
      .eq("id", id)
      .eq("is_deleted", false)
      .select()
      .single();

    if (error) {
      console.error("Error toggling member lift status:", error);
      throw new Error(error.message);
    }

    return {
      id: data.id,
      chitPoolId: data.chit_pool_id,
      name: data.name,
      whatsappNumber: data.whatsapp_number,
      hasLifted: data.has_lifted,
      liftedDate: data.lifted_date ? format(new Date(data.lifted_date), "yyyy-MM-dd") : null,
      disbursedAmount: data.disbursed_amount,
      createdAt: format(new Date(data.created_at), "yyyy-MM-dd'T'HH:mm:ss")
    };
  } catch (error: any) {
    console.error("Error toggling member lift status:", error);
    throw new Error(error.message);
  }
};

// Get all loans
export const getLoans = async (): Promise<Loan[]> => {
  try {
    const { data: loanRows, error: loanError } = await supabase
      .from("loans")
      .select("*")
      .eq("is_deleted", false);

    if (loanError) {
      console.error("Error fetching loans:", loanError);
      throw new Error(loanError.message);
    }

    if (!loanRows) return [];

    // Map LoanRows to Loan type
    const loans: Loan[] = loanRows.map(mapLoanRowToLoan);

    return loans;
  } catch (error: any) {
    console.error("Error fetching loans:", error);
    throw new Error(error.message);
  }
};

// Get loan by ID
export const getLoanById = async (id: string): Promise<Loan | null> => {
  try {
    const { data: loanRow, error: loanError } = await supabase
      .from("loans")
      .select("*")
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (loanError) {
      console.error("Error fetching loan:", loanError);
      throw new Error(loanError.message);
    }

    if (!loanRow) return null;

    // Map LoanRow to Loan type
    const loan: Loan = mapLoanRowToLoan(loanRow);

    return loan;
  } catch (error: any) {
    console.error("Error fetching loan:", error);
    throw new Error(error.message);
  }
};

// Create loan
export const createLoan = async (loan: Omit<Loan, 'id' | 'createdAt' | 'monthlyInterest'>): Promise<Loan> => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("User not authenticated");

    // Calculate monthly interest
    const monthlyInterest = (loan.amount * loan.interest_rate) / 100 / 12;

    const { data, error } = await supabase
      .from("loans")
      .insert([
        {
          customer_name: loan.customer_name,
          whatsapp_number: loan.whatsappNumber,
          amount: loan.amount,
          interest_rate: loan.interest_rate,
          tenure: loan.tenure,
          description: loan.description,
          first_due_date: new Date(loan.firstDueDate).toISOString(),
          monthly_interest: monthlyInterest,
          user_id: user.id,
          is_deleted: false,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error("Error creating loan:", error);
      throw new Error(error.message);
    }

    if (!data) {
      throw new Error("No data returned after creating loan");
    }

    return {
      id: data.id,
      customer_name: data.customer_name,
      whatsappNumber: data.whatsapp_number,
      amount: data.amount,
      interest_rate: data.interest_rate,
      tenure: data.tenure,
      description: data.description,
      monthlyInterest: data.monthly_interest,
      firstDueDate: format(new Date(data.first_due_date), "yyyy-MM-dd"),
      createdAt: format(new Date(data.created_at), "yyyy-MM-dd'T'HH:mm:ss"),
    };
  } catch (error: any) {
    console.error("Error creating loan:", error);
    throw new Error(error.message);
  }
};

// Update loan
export const updateLoan = async (id: string, loan: Partial<Omit<Loan, 'id' | 'createdAt' | 'monthlyInterest'>>): Promise<Loan> => {
  try {
    // Fetch the existing loan from the database
    const { data: existingLoanData, error: existingLoanError } = await supabase
      .from("loans")
      .select("*")
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (existingLoanError) {
      console.error("Error fetching existing loan:", existingLoanError);
      throw new Error(existingLoanError.message);
    }

    if (!existingLoanData) {
      throw new Error("Loan not found");
    }

    // Calculate monthly interest if amount or interest rate are being updated
    let monthlyInterest = existingLoanData.monthly_interest;
    let amount = loan.amount !== undefined ? loan.amount : existingLoanData.amount;
    let interest_rate = loan.interest_rate !== undefined ? loan.interest_rate : existingLoanData.interest_rate;

    if (loan.amount !== undefined || loan.interest_rate !== undefined) {
      monthlyInterest = (amount * interest_rate) / 100 / 12;
    }

    const { data, error } = await supabase
      .from("loans")
      .update({
        customer_name: loan.customer_name,
        whatsapp_number: loan.whatsappNumber,
        amount: amount,
        interest_rate: interest_rate,
        tenure: loan.tenure,
        description: loan.description,
        first_due_date: loan.firstDueDate ? new Date(loan.firstDueDate).toISOString() : undefined,
        monthly_interest: monthlyInterest,
      })
      .eq("id", id)
      .eq("is_deleted", false)
      .select()
      .single();

    if (error) {
      console.error("Error updating loan:", error);
      throw new Error(error.message);
    }

    return {
      id: data.id,
      customer_name: data.customer_name,
      whatsappNumber: data.whatsapp_number,
      amount: data.amount,
      interest_rate: data.interest_rate,
      tenure: data.tenure,
      description: data.description,
      monthlyInterest: data.monthly_interest,
      firstDueDate: format(new Date(data.first_due_date), "yyyy-MM-dd"),
      createdAt: format(new Date(data.created_at), "yyyy-MM-dd'T'HH:mm:ss"),
    };
  } catch (error: any) {
    console.error("Error updating loan:", error);
    throw new Error(error.message);
  }
};

// Delete loan (soft delete)
export const deleteLoan = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from("loans")
      .update({ is_deleted: true })
      .eq("id", id);

    if (error) {
      console.error("Error deleting loan:", error);
      throw new Error(error.message);
    }
  } catch (error: any) {
    console.error("Error deleting loan:", error);
    throw new Error(error.message);
  }
};

// Create reminder
export const createReminder = async (reminder: Omit<Reminder, 'id'>): Promise<Reminder> => {
  try {
    const { data, error } = await supabase
      .from("reminders")
      .insert([
        {
          chit_pool_id: reminder.chitPoolId || null,
          loan_id: reminder.loanId || null,
          message: reminder.message,
          reminder_date: new Date(reminder.reminderDate).toISOString(),
          sent: reminder.sent || false,
          is_deleted: false,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error("Error creating reminder:", error);
      throw new Error(error.message);
    }

    if (!data) {
      throw new Error("No data returned after creating reminder");
    }

    return {
      id: data.id,
      chitPoolId: data.chit_pool_id || undefined,
      loanId: data.loan_id || undefined,
      message: data.message,
      reminderDate: format(new Date(data.reminder_date), "yyyy-MM-dd"),
      sent: data.sent || false,
    };
  } catch (error: any) {
    console.error("Error creating reminder:", error);
    throw new Error(error.message);
  }
};

// Create loan reminder (specialized helper function for loan reminders)
export const createLoanReminder = async (reminderData: Omit<LoanReminder, 'id'>): Promise<LoanReminder> => {
  try {
    // First get user id from the loan to ensure RLS policy passes
    const { data: loanData, error: loanError } = await supabase
      .from("loans")
      .select("user_id")
      .eq("id", reminderData.loanId)
      .eq("is_deleted", false)
      .single();
      
    if (loanError) {
      console.error("Error fetching loan:", loanError);
      throw new Error(loanError.message);
    }
      
    const { data, error } = await supabase
      .from("loan_reminders")
      .insert([
        {
          loan_id: reminderData.loanId,
          reminder_date: new Date(reminderData.reminderDate).toISOString(),
          message: reminderData.message,
          sent: reminderData.sent || false,
          is_deleted: false,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error("Error creating loan reminder:", error);
      throw new Error(error.message);
    }

    if (!data) {
      throw new Error("No data returned after creating loan reminder");
    }

    return {
      id: data.id,
      loanId: data.loan_id,
      reminderDate: format(new Date(data.reminder_date), "yyyy-MM-dd"),
      message: data.message,
      sent: data.sent || false,
    };
  } catch (error: any) {
    console.error("Error creating loan reminder:", error);
    throw new Error(error.message);
  }
};

// Get dashboard data
export const getDashboardData = async () => {
  try {
    // Get the current user
    const user = await getCurrentUser();
    if (!user) throw new Error("User not authenticated");

    // Fetch counts for chit pools and loans
    const { data: chitPoolsData, error: chitPoolsError } = await supabase
      .from("chit_pools")
      .select("id, name, amount")
      .eq("is_deleted", false);

    const { data: loansData, error: loansError } = await supabase
      .from("loans")
      .select("id, customer_name, amount, interest_rate, monthly_interest")
      .eq("is_deleted", false);

    if (chitPoolsError) {
      console.error("Error fetching chit pools count:", chitPoolsError);
      throw new Error(chitPoolsError.message);
    }

    if (loansError) {
      console.error("Error fetching loans count:", loansError);
      throw new Error(loansError.message);
    }

    // Fetch upcoming chit payments
    const { data: upcomingChitPayments, error: upcomingChitPaymentsError } =
      await supabase
        .from("reminders")
        .select("*, chit_pool:chit_pools(name)")
        .not("chit_pool_id", "is", null)
        .eq("is_deleted", false)
        .gte("reminder_date", format(new Date(), "yyyy-MM-dd"))
        .lte("reminder_date", format(addDays(new Date(), 30), "yyyy-MM-dd"));

    if (upcomingChitPaymentsError) {
      console.error(
        "Error fetching upcoming chit payments:",
        upcomingChitPaymentsError
      );
      throw new Error(upcomingChitPaymentsError.message);
    }

    // Fetch upcoming interest payments
    const { data: upcomingInterestPayments, error: upcomingInterestPaymentsError } =
      await supabase
        .from("reminders")
        .select("*, loan:loans(customer_name)")
        .not("loan_id", "is", null)
        .eq("is_deleted", false)
        .gte("reminder_date", format(new Date(), "yyyy-MM-dd"))
        .lte("reminder_date", format(addDays(new Date(), 30), "yyyy-MM-dd"));

    if (upcomingInterestPaymentsError) {
      console.error(
        "Error fetching upcoming interest payments:",
        upcomingInterestPaymentsError
      );
      throw new Error(upcomingInterestPaymentsError.message);
    }

    // Calculate total interest collected
    // This is a placeholder calculation based on loan data
    let totalInterestCollected = 0;
    if (loansData) {
      totalInterestCollected = loansData.reduce((total, loan) => {
        return total + (loan.monthly_interest || 0);
      }, 0);
    }

    return {
      chitPoolsCount: chitPoolsData ? chitPoolsData.length : 0,
      loansCount: loansData ? loansData.length : 0,
      totalInterestCollected: totalInterestCollected,
      upcomingChitPayments: upcomingChitPayments || [],
      upcomingInterestPayments: upcomingInterestPayments || [],
      chitPools: chitPoolsData || [],
      loans: loansData || [],
    };
  } catch (error: any) {
    console.error("Error fetching dashboard data:", error);
    throw new Error(error.message);
  }
};
